import { useEffect, useState } from 'react'
import { data, useFetcher } from 'react-router'
import type { ActionFunctionArgs } from 'react-router'
import {
	type VeriffWebhookPayload,
	createVeriffSession,
	saveVeriffApiLog,
	saveVeriffRecord,
} from '#app/services/veriff.server'
import { requireUser } from '#app/utils/auth.server'
import { db } from '#app/utils/db.server'
import { getDomainUrl } from '#app/utils/misc'

// Helper function to get tenantId from request hostname
async function getTenantIdFromRequest(request: Request): Promise<string> {
	const hostname = new URL(request.url).hostname

	// Try to find existing tenant by hostname
	const existingTenant = await db.query.tenant.findFirst({
		where: (tenant, { eq }) => eq(tenant.hostname, hostname),
	})

	if (existingTenant) {
		return existingTenant.id
	}

	// Fallback to TrueTenant or default tenant
	const trueTenant = await db.query.tenant.findFirst({
		where: (tenant, { eq, or }) =>
			or(
				eq(tenant.name, 'TrueTenant'),
				eq(tenant.hostname, 'localhost'),
				eq(tenant.hostname, 'truetenant.ai'),
			),
	})

	if (!trueTenant) {
		throw new Error(
			'No tenant found for this hostname. Please ensure tenant is configured.',
		)
	}

	return trueTenant.id
}

export async function action({ request }: ActionFunctionArgs) {
	if (request.method !== 'POST') {
		return data({ error: 'Method not allowed' }, { status: 405 })
	}

	try {
		// Get userId from URL query parameter
		const url = new URL(request.url)
		const userId = url.searchParams.get('userId')

		if (!userId) {
			return data(
				{ error: 'userId query parameter is required' },
				{ status: 400 },
			)
		}

		// Get tenantId from request
		const tenantId = await getTenantIdFromRequest(request)

		// Build callback URL
		const domainUrl = getDomainUrl(request)
		const callbackUrl = `${domainUrl}/webhooks/veriff`

		console.log('[Veriff] Creating verification session for userId:', userId)

		// Create Veriff session
		const { verificationUrl, sessionId } = await createVeriffSession(
			userId,
			callbackUrl,
		)

		// Store session-user mapping in user_veriff_records for webhook lookup
		await saveVeriffRecord(userId, sessionId, {
			id: sessionId,
			status: 'pending',
		} as VeriffWebhookPayload)

		// Save API log
		// Note: We don't have the full request/response payloads here,
		// but we can save what we have
		const requestPayload = {
			userId,
			callbackUrl,
		}

		const responsePayload = {
			verificationUrl,
			sessionId,
		}

		await saveVeriffApiLog(userId, tenantId, requestPayload, responsePayload)

		console.log('[Veriff] Verification session created successfully')

		// Return response
		return data(
			{
				verificationUrl,
				sessionId,
			},
			{ status: 200 },
		)
	} catch (error) {
		console.error('[Veriff] Error in veriff-connect action:', error)

		return data(
			{
				error:
					error instanceof Error
						? error.message
						: 'Failed to create Veriff verification session',
			},
			{ status: 500 },
		)
	}
}

type ActionData = {
	verificationUrl?: string
	sessionId?: string
	error?: string
} | null

export default function VeriffConnectRoute() {
	const fetcher = useFetcher<ActionData>()
	const [verificationUrl, setVerificationUrl] = useState<string | null>(null)

	// Handle successful response from action
	useEffect(() => {
		if (fetcher.data && !('error' in fetcher.data)) {
			if (fetcher.data.verificationUrl) {
				setVerificationUrl(fetcher.data.verificationUrl)
			}
		}
	}, [fetcher.data])

	const handleVerify = () => {
		fetcher.submit({}, { method: 'POST' })
	}

	// Show error if there is one
	const error =
		fetcher.data && 'error' in fetcher.data ? fetcher.data.error : null

	// If we have a verification URL, show iframe
	if (verificationUrl) {
		return (
			<div style={styles.wrapper}>
				<iframe
					title="Veriff Identity Verification"
					src={verificationUrl}
					style={styles.iframe}
					allow="camera; microphone; fullscreen"
				/>
			</div>
		)
	}

	// Show loading state
	const isLoading =
		fetcher.state === 'submitting' || fetcher.state === 'loading'

	return (
		<div style={styles.container}>
			<div style={styles.content}>
				<h1 style={styles.title}>Identity Verification</h1>
				<p style={styles.description}>
					Click the button below to start your identity verification process.
					You will be asked to provide identification documents.
				</p>

				{error && (
					<div style={styles.errorContainer}>
						<p style={styles.errorText}>{error}</p>
					</div>
				)}

				<button
					type="button"
					onClick={handleVerify}
					disabled={isLoading}
					style={{
						...styles.button,
						...(isLoading
							? { backgroundColor: '#9ca3af', cursor: 'not-allowed' }
							: {}),
					}}
				>
					{isLoading ? 'Loading...' : 'Verify Your Identity'}
				</button>
			</div>
		</div>
	)
}

const styles = {
	wrapper: {
		width: '100vw',
		height: '100vh',
		position: 'fixed' as const,
		top: 0,
		left: 0,
		border: 'none',
		margin: 0,
		padding: 0,
	},
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
	container: {
		minHeight: '100vh',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#f9fafb',
		padding: '2rem',
	},
	content: {
		backgroundColor: 'white',
		borderRadius: '0.5rem',
		boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
		padding: '3rem',
		maxWidth: '32rem',
		width: '100%',
	},
	title: {
		fontSize: '1.875rem',
		fontWeight: 600,
		color: '#111827',
		marginBottom: '1rem',
		textAlign: 'center' as const,
	},
	description: {
		fontSize: '1rem',
		color: '#6b7280',
		marginBottom: '2rem',
		textAlign: 'center' as const,
		lineHeight: '1.5',
	},
	errorContainer: {
		backgroundColor: '#fef2f2',
		border: '1px solid #fecaca',
		borderRadius: '0.375rem',
		padding: '1rem',
		marginBottom: '1.5rem',
	},
	errorText: {
		color: '#dc2626',
		fontSize: '0.875rem',
	},
	button: {
		width: '100%',
		backgroundColor: '#32302F',
		color: 'white',
		fontSize: '1rem',
		fontWeight: 500,
		padding: '0.75rem 1.5rem',
		borderRadius: '0.375rem',
		border: 'none',
		cursor: 'pointer',
		transition: 'background-color 0.2s',
	},
}
